import React, { ReactNode, useContext } from 'react';
import BulkUploadSelect from 'components/BulkUploadSelect';
import { AddMembersPeopleTableKeys, HEADER_OPTIONS } from './data';
import classNames from 'classnames';
import { checkIsValidEmail, checkIsValidName, isLinkedinUrl } from 'utils';
import {
  ensurePhonePlusPrefix,
  getIsValidPhone,
  getPhone,
} from 'phoneNumberUtils';
import { Checkbox } from 'ncoded-component-library';
import { SheetEnterpriseMember } from 'models/Enterprises';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import { isInvalidEnterpriseArrayValue } from './utils';

type CellProps = {
  column: string;
  member: Record<string, any>;
  updateMember?: (member: Partial<SheetEnterpriseMember>) => void;
};

const MobileCell: React.FC<CellProps> = ({ member }) => {
  const { phoneNumber } = member || {};

  if (!phoneNumber) return null;

  const phone = getPhone(phoneNumber);

  const isInvalid = !getIsValidPhone(phone);

  return (
    <div className={classNames({ 'invalid-cell': isInvalid })}>
      {ensurePhonePlusPrefix(phoneNumber)}
    </div>
  );
};

const EmailCell: React.FC<CellProps> = ({ member }) => {
  const { email } = member || {};

  const isInvalid = !checkIsValidEmail(email);

  return (
    <div className={classNames({ 'invalid-cell': isInvalid })}>{email}</div>
  );
};

const FirstNameCell: React.FC<CellProps> = ({ member }) => {
  const { firstName } = member || {};

  const isInvalid = !checkIsValidName(firstName);

  return (
    <div className={classNames({ 'invalid-cell': isInvalid })}>{firstName}</div>
  );
};

const LastNameCell: React.FC<CellProps> = ({ member }) => {
  const { lastName } = member || {};

  const isInvalid = !checkIsValidName(lastName);

  return (
    <div className={classNames({ 'invalid-cell': isInvalid })}>{lastName}</div>
  );
};

const TitleCell: React.FC<CellProps> = ({ member }) => {
  const { title } = member || {};

  return <div className={classNames({ 'invalid-cell': !title })}>{title}</div>;
};

const UnitCell: React.FC<CellProps> = ({ column, member }) => {
  const { units } = useContext(EnterpriseMembersContext);

  const el = member[column];

  const isInvalid = isInvalidEnterpriseArrayValue(el, units);

  return <div className={classNames({ 'invalid-cell': isInvalid })}>{el}</div>;
};

const SeniorityCell: React.FC<CellProps> = ({ column, member }) => {
  const { memberSeniorities } = useContext(EnterpriseMembersContext);

  const el = member[column];

  const isInvalid = isInvalidEnterpriseArrayValue(el, memberSeniorities);

  return <div className={classNames({ 'invalid-cell': isInvalid })}>{el}</div>;
};

const LocationCell: React.FC<CellProps> = ({ column, member }) => {
  const { locations } = useContext(EnterpriseMembersContext);

  const el = member[column];

  const isInvalid = isInvalidEnterpriseArrayValue(el, locations);

  return <div className={classNames({ 'invalid-cell': isInvalid })}>{el}</div>;
};

const LinkedInCell: React.FC<CellProps> = ({ column, member }) => {
  const el = member[column];

  if (!el) return null;

  const isInvalid = !isLinkedinUrl(el);

  return <div className={classNames({ 'invalid-cell': isInvalid })}>{el}</div>;
};

const OfficeManagerCell: React.FC<CellProps> = ({
  column,
  member,
  updateMember,
}) => {
  const el = member[column];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Checkbox
        checked={!!el}
        onChange={(isOfficeManager) =>
          updateMember({ _id: member._id, isOfficeManager: !!isOfficeManager })
        }
      />
    </div>
  );
};

export const KeyCell: React.FC<CellProps> = ({ column, member }) => {
  const el = member[column];

  return <div>{el}</div>;
};

export const BODY_CONTENT_GENERATE: Record<
  AddMembersPeopleTableKeys,
  (cellProps: CellProps) => ReactNode
> = {
  firstName: (cellProps: CellProps) => <FirstNameCell {...cellProps} />,
  lastName: (cellProps: CellProps) => <LastNameCell {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  title: (cellProps: CellProps) => <TitleCell {...cellProps} />,
  phoneNumber: (cellProps: CellProps) => <MobileCell {...cellProps} />,
  businessUnitName: (cellProps: CellProps) => <UnitCell {...cellProps} />,
  seniority: (cellProps: CellProps) => <SeniorityCell {...cellProps} />,
  linkedinAccount: (cellProps: CellProps) => <LinkedInCell {...cellProps} />,
  location: (cellProps: CellProps) => <LocationCell {...cellProps} />,
  isOfficeManager: (cellProps: CellProps) => (
    <OfficeManagerCell {...cellProps} />
  ),
};

type HeaderProps = {
  column: string;
  index?: number;
  allColumns: string[];
  onUpdateColumn?: (key: string, newValue: string) => void;
};

export const KeyHeader: React.FC<HeaderProps> = (props) => (
  <BulkUploadSelect {...props} options={HEADER_OPTIONS} />
);
